import React, { useState } from 'react';
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  UseFormSetValue,
} from 'react-hook-form';
import { FormField, Input } from '@legalshield/adonis-ux-framework';

import { fetchZipCodeData } from '../queriesClient';
import TextField from './TextField';

const CA_POSTAL = /^\s*[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$|^[A-Za-z]\d[A-Za-z]\s*$/;
const US_ZIP = /^\s*\d{5}\s*$/;
const isValidZip = (value: string) => US_ZIP.test(value) || CA_POSTAL.test(value);

const ZIP_HINT = 'Please enter a valid 5-digit zip code or Canadian postal code';
const INVALID_ZIP_HINT = 'Could not find a location for this zip/postal code';

type Props<T extends FieldValues> = {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  namePrefix: Path<T>;
  required?: boolean;
};

type ZipFieldProps<T extends FieldValues> = {
  field: ControllerRenderProps<T, Path<T>>;
  fieldState: ControllerFieldState;
  zipName: Path<T>;
  cityName: Path<T>;
  stateName: Path<T>;
  setValue: UseFormSetValue<T>;
  required?: boolean;
};

const ZipField = <T extends FieldValues>({
  field,
  fieldState,
  zipName,
  cityName,
  stateName,
  setValue,
  required,
}: ZipFieldProps<T>) => {
  const value = (field.value as string | undefined) ?? '';
  const [lookupHint, setLookupHint] = useState<string | null>(null);
  const [lookupStatus, setLookupStatus] = useState<'invalid' | undefined>(undefined);

  const runLookup = async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    if (!isValidZip(trimmed)) {
      setLookupHint(ZIP_HINT);
      setLookupStatus('invalid');
      return;
    }
    setLookupHint(null);
    setLookupStatus(undefined);
    const data = await fetchZipCodeData(trimmed);
    if (!data) {
      setLookupHint(INVALID_ZIP_HINT);
      setLookupStatus('invalid');
      return;
    }
    setValue(cityName, (data.city ?? '') as T[Path<T>], { shouldValidate: true, shouldDirty: true });
    setValue(stateName, (data.stateOrProvince ?? '') as T[Path<T>], {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <FormField
      id={String(zipName)}
      label="Zip/Postal Code"
      required={required}
      status={fieldState.error ? 'invalid' : lookupStatus}
      validationHint={fieldState.error?.message ?? lookupHint ?? undefined}
    >
      <Input
        placeholder="Zip/Postal Code"
        type="text"
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setLookupHint(null);
          setLookupStatus(undefined);
          field.onChange(e.target.value);
        }}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          field.onBlur();
          void runLookup(e.target.value);
        }}
        status={fieldState.error ? 'invalid' : lookupStatus}
      />
    </FormField>
  );
};

const AddressFormField = <T extends FieldValues>({ control, setValue, namePrefix, required }: Props<T>) => {
  const zipName = `${String(namePrefix)}.zipCode` as Path<T>;
  const cityName = `${String(namePrefix)}.city` as Path<T>;
  const stateName = `${String(namePrefix)}.state` as Path<T>;

  return (
    <div className="rnq-field-group">
      <TextField
        control={control}
        name={`${String(namePrefix)}.address1` as Path<T>}
        label="Address Line 1"
        required={required}
      />
      <TextField control={control} name={`${String(namePrefix)}.address2` as Path<T>} label="Address Line 2" />

      <Controller
        control={control}
        name={zipName}
        render={({ field, fieldState }) => (
          <ZipField
            field={field}
            fieldState={fieldState}
            zipName={zipName}
            cityName={cityName}
            stateName={stateName}
            setValue={setValue}
            required={required}
          />
        )}
      />

      <div className="rnq-field-row">
        <TextField control={control} name={cityName} label="City" required={required} />
        <TextField control={control} name={stateName} label="State/Province/Territory" required={required} />
      </div>
    </div>
  );
};

export default AddressFormField;
